/**
 * マニュアル本体の編集(#203)の、画面側で共有する純ロジック。
 * 「マニュアルを編集」画面とレビュー画面の工程ごとの修正(#219)が同じ記録を読み書きする。
 * 鍵と項目名は CLI(src/core/manual-edits.ts)と同じ。
 */
import { answerDocId } from "./review";

export interface EditDoc {
  target: "step" | "dish" | "ingredient" | "prerequisite";
  key: string;
  field: string;
  value: string;
}

/** 記録済みの編集の文書ID。CLI と同じ鍵を Firestore が使える形に丸める */
export function editDocId(target: string, key: string, field: string): string {
  return answerDocId(`${target}:${key}:${field}`);
}

/** 工程の本文のうち、編集できる項目を持つ形(analysis.json の steps[] の部分集合) */
export interface EditableStepFields {
  name: string;
  actions: string;
  heat_level: string | null;
  tools: string[];
  critical_points: string[];
  common_mistakes?: string[];
  completion_criteria: { criteria: string | null } | null;
  taste_check?: { criteria: string | null } | null;
}

/** 工程の項目 → 入力欄に出す文字列(一覧は1行1件) */
export function stepFieldValue(st: EditableStepFields, field: string): string {
  switch (field) {
    case "name":
      return st.name;
    case "actions":
      return st.actions;
    case "heat_level":
      return st.heat_level ?? "";
    case "tools":
      return st.tools.join("\n");
    case "critical_points":
      return st.critical_points.join("\n");
    case "common_mistakes":
      return (st.common_mistakes ?? []).join("\n");
    case "completion_criteria":
      return st.completion_criteria?.criteria ?? "";
    case "taste_check":
      return st.taste_check?.criteria ?? "";
    default:
      return "";
  }
}

const lines = (v: string) =>
  v
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * 記録済み(未反映)の編集を工程の表示に重ねる。本文は反映まで変わらないので、
 * 直した内容がその場で読めるようにするための表示用。適用規則は CLI の applyToStep に合わせる
 */
export function withStepEdits<T extends EditableStepFields>(
  st: T,
  key: string,
  edits: Record<string, EditDoc>,
): T {
  const get = (field: string) => edits[editDocId("step", key, field)]?.value;
  const out: T = { ...st };
  const name = get("name");
  if (name !== undefined && name.trim()) out.name = name.trim();
  const actions = get("actions");
  if (actions !== undefined) out.actions = actions.trim();
  const heat = get("heat_level");
  if (heat !== undefined) out.heat_level = heat.trim() || null;
  const tools = get("tools");
  if (tools !== undefined) out.tools = lines(tools);
  const cps = get("critical_points");
  if (cps !== undefined) out.critical_points = lines(cps);
  const cms = get("common_mistakes");
  if (cms !== undefined) out.common_mistakes = lines(cms);
  const cc = get("completion_criteria");
  if (cc !== undefined) out.completion_criteria = cc.trim() ? { criteria: cc.trim() } : null;
  return out;
}

/** その工程に記録済みの編集があるか */
export function hasStepEdits(key: string, edits: Record<string, EditDoc>): boolean {
  return Object.values(edits).some((e) => e.target === "step" && e.key === key);
}

/** 材料の本文のうち、レビュー画面で直せる項目(analysis.json の ingredients_master[] の部分集合) */
export interface EditableIngredientFields {
  name: string;
  quantity: number | null;
  unit: string | null;
  note: string | null;
  /** 材料表の「— 」以降の説明(下処理・何から作るか)。古い本文には無いことがある */
  prep_note?: string | null;
}

/**
 * 材料で直せる項目(#230)。raw_materials は RawMaterialsPanel が扱う。
 * prep_note は材料表の「— 」以降の説明(CLI の INGREDIENT_EDIT_FIELDS は当初から受け付けている)
 */
export const INGREDIENT_FIELDS = ["name", "quantity", "unit", "note", "prep_note"] as const;
export type IngredientField = (typeof INGREDIENT_FIELDS)[number];

/** 材料の項目 → 入力欄に出す文字列 */
export function ingredientFieldValue(m: EditableIngredientFields, field: IngredientField): string {
  switch (field) {
    case "name":
      return m.name;
    case "quantity":
      return m.quantity != null ? String(m.quantity) : "";
    case "unit":
      return m.unit ?? "";
    case "note":
      return m.note ?? "";
    case "prep_note":
      return m.prep_note ?? "";
  }
}

/**
 * 記録済み(未反映)の材料の修正を表示に重ねる(#230)。鍵は本文の材料名。
 * 適用規則は CLI の applyManualEdits(材料)に合わせる
 */
export function withIngredientEdits<T extends EditableIngredientFields>(
  m: T,
  edits: Record<string, EditDoc>,
): T {
  const get = (field: string) => edits[editDocId("ingredient", m.name, field)]?.value;
  const out: T = { ...m };
  const name = get("name");
  if (name !== undefined && name.trim()) out.name = name.trim();
  const qty = get("quantity");
  if (qty !== undefined) {
    const n = Number(qty.trim());
    out.quantity = qty.trim() && Number.isFinite(n) ? n : null;
  }
  const unit = get("unit");
  if (unit !== undefined) out.unit = unit.trim() || null;
  const note = get("note");
  if (note !== undefined) out.note = note.trim() || null;
  // 空にしたら説明を消す(CLI の orNull と同じ)
  const prep = get("prep_note");
  if (prep !== undefined) out.prep_note = prep.trim() || null;
  return out;
}

/** その材料に名前・分量・単位・備考・説明の修正が記録済みか(原料は別に示す) */
export function hasIngredientEdits(name: string, edits: Record<string, EditDoc>): boolean {
  return INGREDIENT_FIELDS.some((f) => !!edits[editDocId("ingredient", name, f)]);
}

/**
 * 材料の修正の入力を確かめる。問題があれば理由(日本語。画面で t() に通す)を返す。
 * CLI は空の名前・他の材料と同じ名前・数値でない分量を黙って当てないので、保存前に止める
 */
export function ingredientEditError(
  values: Record<IngredientField, string>,
  self: string,
  others: readonly string[],
): string | null {
  const name = values.name.trim();
  if (!name) return "名前を入力してください";
  if (name !== self.trim() && others.some((o) => o.trim() === name)) return "同じ名前の材料が既にあります";
  const qty = values.quantity.trim();
  if (qty && !Number.isFinite(Number(qty))) return "分量は数値で入力してください（単位は右の欄に）";
  return null;
}

/** レシピ文書から取り込んだ仕込みの作り方(#250)。analysis.json の prerequisites[].procedure */
export interface PrepProcedure {
  ingredients: { name: string; amount: string | null }[];
  steps: string[];
  notes: string[];
}

/**
 * 仕込みの作り方の編集値(#259)を読む。CLI の parsePrepProcedure と同じ規則:
 * 空の行は落とし、手順が1つも無ければ null(本文へ当たらない)
 */
export function parsePrepProcedure(value: string): PrepProcedure | null {
  try {
    const v = JSON.parse(value) as Partial<PrepProcedure>;
    const steps = (Array.isArray(v.steps) ? v.steps : []).map((x) => String(x).trim()).filter(Boolean);
    if (steps.length === 0) return null;
    return {
      ingredients: (Array.isArray(v.ingredients) ? v.ingredients : [])
        .map((i) => ({ name: String(i?.name ?? "").trim(), amount: String(i?.amount ?? "").trim() || null }))
        .filter((i) => i.name),
      steps,
      notes: (Array.isArray(v.notes) ? v.notes : []).map((x) => String(x).trim()).filter(Boolean),
    };
  } catch {
    return null;
  }
}

/** 比較・保存用に正規化して JSON にする(同じ内容なら同じ文字列になる) */
export function serializePrepProcedure(p: PrepProcedure): string {
  return JSON.stringify({
    ingredients: p.ingredients
      .map((i) => ({ name: i.name.trim(), amount: i.amount?.trim() || null }))
      .filter((i) => i.name),
    steps: p.steps.map((x) => x.trim()).filter(Boolean),
    notes: p.notes.map((x) => x.trim()).filter(Boolean),
  });
}

/** 記録済み(未反映)の作り方の修正を重ねる。読めない記録は無視して本文の値を出す */
export function withProcedureEdit(
  name: string,
  current: PrepProcedure | null | undefined,
  edits: Record<string, EditDoc>,
): { procedure: PrepProcedure | null; pending: boolean } {
  const e = edits[editDocId("prerequisite", name, "procedure")];
  const edited = e ? parsePrepProcedure(e.value) : null;
  return edited ? { procedure: edited, pending: true } : { procedure: current ?? null, pending: false };
}
